import { getSectionKeyLabel } from "@/modules/cms/domain/cms-labels";
import { isFixedCmsSectionKey } from "@/modules/cms/domain/contracts";
import { formatDateLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import { Button } from "@devhop/ui";
import { EditIcon, GripVerticalIcon, TrashIcon } from "lucide-react";
import type { CmsSectionDTO } from "../api/client";

type CmsSectionsTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: CmsSectionDTO[];
  onEdit: (row: CmsSectionDTO) => void;
  onDelete: (id: string) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
};

export function CmsSectionsTable({
  canManage,
  isLoading,
  data,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}: CmsSectionsTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center border-t p-8 text-muted-foreground">
        ກຳລັງໂຫຼດ...
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <div className="border-t p-8 text-center text-muted-foreground">
        ບໍ່ມີ section. ກົດ &quot;ສ້າງ Section&quot; ເພື່ອເພີ່ມ.
      </div>
    );
  }

  return (
    <div className="flex flex-col border-t">
      {data.map((row, index) => {
        const fixed = isFixedCmsSectionKey(row.sectionKey);
        return (
          <div
            key={row.id}
            className="flex items-center gap-3 border-b px-4 py-3 last:border-b-0"
          >
            {canManage && !fixed && (
              <div className="flex shrink-0 items-center gap-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onMoveUp(index)}
                  disabled={index === 0}
                  aria-label="ຂຶ້ນ"
                >
                  ↑
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onMoveDown(index)}
                  disabled={index === data.length - 1}
                  aria-label="ລົງ"
                >
                  ↓
                </Button>
                <GripVerticalIcon className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            {canManage && fixed && (
              <div className="w-10 shrink-0" aria-hidden />
            )}
            <div className="min-w-0 flex-1">
              <span className="font-medium">
                {getSectionKeyLabel(row.sectionKey)}
              </span>
              <span className="ml-2 text-muted-foreground text-sm">
                ລຳດັບ {row.sortOrder}
              </span>
              <span className="ml-2 text-muted-foreground text-sm">
                · {formatDateLocal(row.updatedAt)}
              </span>
            </div>
            {canManage && (
              <RowActions
                actions={[
                  {
                    label: "ແກ້ໄຂ",
                    icon: <EditIcon className="h-4 w-4" />,
                    onClick: () => onEdit(row),
                  },
                  ...(fixed
                    ? []
                    : [
                        {
                          label: "ລຶບ",
                          icon: <TrashIcon className="h-4 w-4" />,
                          onClick: () => onDelete(row.id),
                          variant: "destructive" as const,
                        },
                      ]),
                ]}
                maxInline={2}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
